import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLine } from "@/components/motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Ladder() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  usePageMeta(
    "The Bean Ladder | Give & Grind",
    "Live UK venues ranked by charity impact — every cup donates 22p. Updated every Monday."
  );

  useEffect(() => {
    api.get("/ladder").then((r) => setData(r.data)).catch(() => setData({ locations: [] }));
  }, []);

  const locs = (data?.locations || []).filter((l) => filter === "all" || l.status === filter);

  return (
    <div data-testid="ladder-page" className="bg-[#F7F5F0] min-h-screen">
      <Nav dark />
      <section className="pt-36 pb-14 bg-[#0B1D16] text-[#FAF9F6] grain relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-[380px] h-[380px] rounded-full bg-[#D97706]/15 blur-[110px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <MaskedLine delay={0.1}>
            <span className="inline-flex items-center gap-2 text-xs font-mono-brand font-semibold uppercase tracking-[0.22em] text-[#D97706] border border-[#D97706]/40 rounded-full px-4 py-2">
              Launching soon — first machines going live
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="ladder-headline">
            <MaskedLine delay={0.25}>The Bean Ladder.</MaskedLine>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#FAF9F6]/60 max-w-xl">
            Every live Give &amp; Grind location, ranked by total raised for charity. {data?.update_note || "Updated every Monday from machine sales reports."}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex gap-2" data-testid="ladder-filters">
              {[["all", "All"], ["live", "Live"], ["coming_soon", "Coming soon"]].map(([v, label]) => (
                <button key={v} data-testid={`ladder-filter-${v}`} onClick={() => setFilter(v)}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-colors duration-300 ${
                    filter === v ? "bg-[#0F2E23] text-[#F7F5F0]" : "bg-white border border-[#E2DDD3] text-[#52665E] hover:border-[#1B5E46]/40"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
            {data?.illustrative && (
              <p className="text-xs font-mono-brand text-[#D97706] uppercase tracking-[0.14em]" data-testid="ladder-illustrative-note">
                Illustrative preview until machine #01 goes live
              </p>
            )}
          </div>
        </Reveal>

        {!data ? (
          <p className="text-sm text-[#52665E]" data-testid="ladder-loading">Loading the ladder…</p>
        ) : locs.length === 0 ? (
          <div className="bg-white rounded-3xl border border-dashed border-[#1B5E46]/40 p-14 text-center" data-testid="ladder-empty">
            <p className="font-display font-bold text-2xl text-[#0B1E17]">Launching soon</p>
            <p className="mt-3 text-sm text-[#52665E] max-w-md mx-auto">
              The Bean Ladder switches on the moment machine #01 pours its first cup. One honest month of data first — a weak ladder is worse than no ladder.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ladder-rows">
            {locs.map((l, i) => (
              <Reveal key={l.id || l.slug} delay={i * 0.06}>
                <Link to={`/l/${l.slug}?src=web`} data-testid={`ladder-row-${l.slug}`}
                  className="group grid grid-cols-[auto_1fr_auto] sm:grid-cols-[56px_auto_1fr_auto_auto] items-center gap-4 sm:gap-6 bg-white rounded-2xl border border-[#E2DDD3] px-5 sm:px-7 py-5 hover:border-[#1B5E46]/40 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#1B5E46]/10 transition-all duration-400">
                  <p className="font-mono-brand font-semibold text-lg text-[#D97706] w-10">
                    {l.status === "live" ? `#${l.rank}` : "—"}
                  </p>
                  {l.logo_url && (
                    <img src={`${BACKEND}${l.logo_url}`} alt={`${l.charity} logo`} data-testid={`ladder-logo-${l.slug}`}
                      className="hidden sm:block w-11 h-11 rounded-xl object-contain bg-[#F7F5F0] border border-[#E2DDD3]" />
                  )}
                  <div>
                    <p className="font-display font-bold text-lg text-[#0B1E17] group-hover:text-[#1B5E46] transition-colors">{l.name}</p>
                    <p className="text-xs text-[#52665E] mt-0.5">{l.city} · supporting {l.charity}</p>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="font-display font-extrabold text-xl text-[#0B1E17]">{fmt(l.raised)}</p>
                    <p className="text-xs text-[#52665E]">{l.cups.toLocaleString()} cups</p>
                  </div>
                  <span className={`text-[10px] font-mono-brand font-semibold uppercase tracking-[0.14em] rounded-full px-3 py-1.5 ${
                    l.status === "live" ? "bg-[#34D399]/15 text-[#1B5E46]" : "bg-[#D97706]/10 text-[#D97706]"
                  }`}>
                    {l.status === "live" ? "Live" : "Coming soon"}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.2}>
          <div className="mt-14 bg-[#0F2E23] text-[#FAF9F6] rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 grain relative overflow-hidden">
            <div>
              <p className="font-display font-bold text-2xl">Want your venue on the ladder?</p>
              <p className="mt-2 text-sm text-[#FAF9F6]/60 max-w-md">Zero cost to host. You pick the charity. We do the rest.</p>
            </div>
            <a href="/#enquire" data-testid="ladder-cta"
              className="shrink-0 rounded-full bg-[#D97706] text-white font-bold px-7 py-3.5 text-sm hover:bg-[#F59E0B] transition-colors duration-300">
              Get a machine
            </a>
          </div>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
